// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../config/firebase';

const AuthContext = createContext(null);

// Eternal fallback — this UID can never be locked out regardless of Firestore state.
const OWNER_UID = 'IOj8t19zqBRJynu45HRKqf8J0MB2';

/**
 * Look up a person by email in a Firestore collection (/students or /staff).
 * Returns the matched doc as { id, ...data } or null.
 *
 * Email matching is case-insensitive and trim-tolerant. Fails CLOSED on
 * Firestore error.
 */
async function findInCollection(collectionName, email) {
  if (!email) return null;
  const target = email.trim().toLowerCase();
  try {
    const snap = await getDocs(collection(db, collectionName));
    let match = null;
    snap.forEach((d) => {
      if (match) return;
      const data = d.data() || {};
      const docEmail = (data.email || '').trim().toLowerCase();
      if (docEmail && docEmail === target) {
        match = { id: d.id, ...data };
      }
    });
    return match;
  } catch (err) {
    console.error(`findInCollection(${collectionName}): lookup failed:`, err);
    return null;
  }
}

/**
 * Read the system settings doc once to check whether student logins are
 * currently enabled. When the flag is missing/false, students are blocked.
 *
 * Fails CLOSED — if we can't read the flag, students are blocked.
 */
async function isStudentLoginEnabled() {
  try {
    const snap = await getDoc(doc(db, 'config', 'systemSettings'));
    if (!snap.exists()) return false;
    return !!snap.data().studentLoginEnabled;
  } catch (err) {
    console.error('isStudentLoginEnabled: read failed, failing closed:', err);
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // True while we're resolving a fresh login's role. Downstream gates
  // (ProtectedRoute, StudentRoute) wait until this is false to make
  // routing decisions, preventing a brief faculty-UI flash for students
  // during the lookup phase. Goes true at sign-in, false once role is
  // stamped (or the user is signed out for rejection).
  const [roleResolving, setRoleResolving] = useState(false);

  // When non-null, the user attempted to sign in but was blocked. The Login
  // page reads this and shows a friendly error.
  const [blockReason, setBlockReason] = useState(null);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (err) {
      console.error('Sign-in error:', err);
      throw err;
    }
  };

  const logout = () => signOut(auth);

  const clearBlockReason = () => setBlockReason(null);

  /**
   * Write the /users/{uid} doc with a given role. Used at multiple points
   * during the login flow:
   *
   *   1. Initial "temp faculty" stamp on fresh login — gets the doc
   *      created so /students and /staff reads succeed.
   *   2. Final role stamp once membership is determined.
   *
   * Preserves existing 'superadmin' role (never demote a superadmin via
   * the login flow).
   */
  const writeUserDoc = async (firebaseUser, derivedRole, extra = {}) => {
    if (!firebaseUser) return;
    const ref = doc(db, 'users', firebaseUser.uid);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : null;

    // SuperAdmin is sticky — never demoted via login flow.
    const finalRole = existing?.role === 'superadmin' ? 'superadmin' : derivedRole;

    const baseFields = {
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      role: finalRole,
      lastLogin: serverTimestamp(),
      ...extra,
    };

    if (!snap.exists()) {
      await setDoc(ref, {
        uid: firebaseUser.uid,
        ...baseFields,
        allowedTools: [],
        createdAt: serverTimestamp(),
      });
    } else {
      // Merge so we keep checkoutSections, staffSisId, etc. that other
      // systems write asynchronously.
      await setDoc(ref, baseFields, { merge: true });
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists()) setProfile(snap.data());
  };

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setProfile(null);
        setRoleResolving(false);
        setLoading(false);
        return;
      }

      // We've authenticated. The flow branches based on whether this is a
      // returning user (already has a /users doc with a settled role) or a
      // brand-new user (no doc yet):
      //
      //  Returning user:
      //    Just refresh lastLogin. Their role is already settled — we don't
      //    re-verify membership on every login. (Re-verifying would require
      //    writing a temp role and reading /staff and /students, which
      //    breaks for returning students because the rules block them from
      //    flipping their role back to 'faculty' for the temp write.)
      //
      //  New user:
      //    1. Stamp a temp 'faculty' /users doc so /students and /staff reads
      //       pass the rules.
      //    2. Look up the user in /students and /staff.
      //    3. Finalize the role based on what we found.
      //    4. If they're in neither, delete the temp doc and sign them out.
      //
      // Throughout, roleResolving stays true so gates don't render the
      // wrong UI for the lookup window.

      setUser(firebaseUser);
      setRoleResolving(true);

      const isOwner = firebaseUser.uid === OWNER_UID;
      const userRef = doc(db, 'users', firebaseUser.uid);

      // Check whether this is a returning user (has a /users doc).
      let existingDoc = null;
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) existingDoc = snap.data();
      } catch (err) {
        console.error('Could not read /users doc:', err);
      }

      // ── Returning user path ────────────────────────────────────────────
      // Already has a settled role. Refresh lastLogin and basic identity
      // fields without touching role.
      if (existingDoc) {
        try {
          await setDoc(
            userRef,
            {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              lastLogin: serverTimestamp(),
            },
            { merge: true },
          );
        } catch (err) {
          // Non-fatal — they can use the app even if lastLogin didn't refresh.
          console.warn('lastLogin refresh failed:', err);
        }
        setRoleResolving(false);
        return;
      }

            // ── New user path: stamp temp PENDING, look up, finalize ───────────
      try {
        await writeUserDoc(firebaseUser, 'pending');
      } catch (err) {
        console.error('Initial /users write failed:', err);
        setBlockReason(
          'Could not initialize your session. Please try again. If this ' +
            'keeps happening, contact IT.',
        );
        await signOut(auth);
        setRoleResolving(false);
        return;
      }

      // Owner short-circuit: no lookup needed.
      if (isOwner) {
        setRoleResolving(false);
        return;
      }

      // Look up membership in both collections in parallel.
      let studentMatch = null;
      let staffMatch = null;
      try {
        [studentMatch, staffMatch] = await Promise.all([
          findInCollection('students', firebaseUser.email),
          findInCollection('staff', firebaseUser.email),
        ]);
      } catch (err) {
        console.error('Membership lookup failed:', err);
      }

      // Decide role. Student wins if in both (unusual, but a student record
      // is more identity-defining than a staff record).
      if (studentMatch) {
        const enabled = await isStudentLoginEnabled();
        if (!enabled) {
          // Student detected but flag is off. Delete the temp doc so they
          // don't linger in /users with role:'faculty'.
          try {
            await deleteDoc(userRef);
          } catch (err) {
            console.warn('Failed to delete temp /users doc on reject:', err);
          }
          setBlockReason(
            'Student logins are not yet enabled. If you believe this is ' +
              'an error, please contact your advisor or IT.',
          );
          await signOut(auth);
          setRoleResolving(false);
          return;
        }

        // Student login allowed — stamp final role.
        try {
          await writeUserDoc(firebaseUser, 'student', {
            studentSisId: String(studentMatch.sisId || studentMatch.id || ''),
          });
        } catch (err) {
          console.error('Final student write failed:', err);
          setBlockReason(
            'Could not initialize your student profile. Please try again. ' +
              'If this keeps happening, contact IT.',
          );
          await signOut(auth);
          setRoleResolving(false);
          return;
        }
        setRoleResolving(false);
        return;
      }

      if (staffMatch) {
        // Already stamped as faculty in the temp write; just add staffSisId.
        try {
          await writeUserDoc(firebaseUser, 'faculty', {
            staffSisId: String(staffMatch.sisId || staffMatch.id || ''),
          });
        } catch (err) {
          console.error('Final staff write failed:', err);
          // Not fatal — they're already signed in as faculty without
          // staffSisId. The autoAssignCheckoutOnUserCreate trigger will
          // populate it on next user-doc-create event (already fired
          // when temp doc was written).
        }
        setRoleResolving(false);
        return;
      }

      // Not in either roster — reject. Clean up the temp doc and sign out.
      //
      // CRITICAL: the temp /users doc MUST be deleted before signOut. If
      // deleteDoc fails for any reason (rules denial, Firestore error,
      // network) we retry up to 3 times. If it still fails, we log loudly
      // and DO NOT sign out — leaving the user in a half-state that they'll
      // notice (vs. silently orphaning a /users doc with role:'faculty'
      // that grants them faculty access on next login).
      //
      // Background: this exists because tomorrow ~400 students log in, and
      // any unknown email (typo, wrong account chosen, alum) needs to be
      // rejected cleanly without leaking faculty role.
      let deleteSucceeded = false;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          await deleteDoc(userRef);
          deleteSucceeded = true;
          break;
        } catch (err) {
          console.error(
            `[REJECT] deleteDoc attempt ${attempt}/3 failed:`,
            err.code, err.message,
          );
          if (attempt < 3) {
            // Brief backoff before retry
            await new Promise((res) => setTimeout(res, 200 * attempt));
          }
        }
      }

      if (!deleteSucceeded) {
        // Couldn't clean up the temp doc. To avoid leaking faculty access,
        // try to neutralize the doc by flipping its role to 'rejected'.
        // Uses merge:true to keep other fields intact (which keeps the
        // affectedKeys check in the rules happy).
        try {
          await setDoc(
            userRef,
            {
              role: 'rejected',
              rejectedAt: serverTimestamp(),
              rejectedReason: 'not_in_directory',
            },
            { merge: true },
          );
          console.warn(
            '[REJECT] Could not delete /users doc; marked as role:rejected',
          );
        } catch (overrideErr) {
          console.error(
            '[REJECT] CRITICAL: could not delete OR neutralize /users doc:',
            overrideErr,
          );
          setBlockReason(
            "Sign-in could not be completed. Please screenshot this page " +
              "and contact IT immediately. Your account may need to be " +
              "manually reviewed.",
          );
          // Do NOT sign out — leave them stranded with the error message.
          // A SuperAdmin will see the half-state in Firestore and can fix.
          setRoleResolving(false);
          return;
        }
      }

      setBlockReason(
        "Your email is not registered in the King's Academy directory. " +
          'If you believe this is an error, please contact IT to be added ' +
          'to the appropriate roster.',
      );
      await signOut(auth);
      setRoleResolving(false);
    });
    return unsubAuth;
  }, []);

  // Subscribe to the user's profile document so changes (role finalization
  // during login, role promotions, tool grants, etc.) flow live into context.
  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      return undefined;
    }

    const ref = doc(db, 'users', user.uid);
    const unsubProfile = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setProfile(snap.data());
        } else {
          setProfile(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Profile subscription error:', err);
        setLoading(false);
      },
    );
    return () => unsubProfile();
  }, [user?.uid]);

  // SuperAdmin if: hardcoded owner UID (eternal fallback) OR Firestore
  // role='superadmin'.
  const isSuperAdmin =
    user?.uid === OWNER_UID ||
    profile?.role === 'superadmin';

  // Student if profile.role === 'student'. Mutually exclusive with
  // isSuperAdmin since owner UID never gets role='student'.
  const isStudent = profile?.role === 'student' && !isSuperAdmin;

    // Dean if profile.role === 'dean'. Faculty-plus: gets faculty-level tool
  // access AND elevated visibility in the Advisory tool (enforced server-side).
  // Mutually exclusive with isSuperAdmin.
  const isDean = profile?.role === 'dean' && !isSuperAdmin;

  const value = {
    user,
    profile,
    loading,
    roleResolving,
    blockReason,
    clearBlockReason,
    signInWithGoogle,
    logout,
    refreshProfile,
    isSuperAdmin,
    isStudent,
    isDean,
    studentSisId: profile?.studentSisId
      ? String(profile.studentSisId)
      : null,
    role: profile?.role ?? 'faculty',
    allowedTools: profile?.allowedTools ?? [],
    department: profile?.department ?? null,
    departmentReviewAccess: profile?.departmentReviewAccess ?? null,
    devTools: profile?.devTools === true,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};